<template>
  <el-container>
    <el-main>
      <h3 class="heading">Edit campaign</h3>
      <el-card>
        <CampaignForm
          v-if="campaign"
          v-model="campaign"
          :loading="loading"
          action-text="Update"
          @submit="onSubmit"
        />
      </el-card>
      <LoadingBlock :loading="fetchingCampaign" />
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, provide, reactive, ref, watch, computed } from 'vue'
import { useCampaignsStore } from '@/apps/traforama/store/campaigns'

import { CampaignValidationErrorsKey } from '@/api/types'
import { ICampaignAttributes } from '@/api/campaigns'

import useRouter from '@/hooks/useRouter'
import { ElMessage } from 'element-plus'
import CampaignForm from '../components/CampaignForm.vue'
import LoadingBlock from '@/components/LoadingBlock.vue'
import useValidation from '@/hooks/useValidation'
import useFetchCampaign from '@/hooks/useFetchCampaign'
import { safeSubmitProcessing } from '@/utils/helpers'
import { useIPListsStore } from '@/apps/traforama/store/ipLists'
import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'
import { useRetargetingListsStore } from '@/apps/traforama/store/retargetingLists'

import { ElMessageBox } from 'element-plus'

export default defineComponent({
  name: 'EditCampaign',
  components: {
    CampaignForm,
    LoadingBlock
  },
  props: {
    id: {
      type: String,
      required: true
    },
    domain: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const campaignsStore = useCampaignsStore()
    const spotsListsStore = useSpotsListsStore()
    const ipListsStore = useIPListsStore()
    const retargetingListsStore = useRetargetingListsStore()

    ipListsStore.FetchItems({})
    retargetingListsStore.FetchItems({})

    const { provideValidation, setValidationErrors } = useValidation(
      CampaignValidationErrorsKey
    )
    const errors = provideValidation()

    const loading = ref(false)

    const { campaign, fetchingCampaign, fetchCampaign } = useFetchCampaign()

    fetchCampaign(props.id)

    const campaignData = computed(() => {
      return campaignsStore.itemById(props.id)
    })

    const { router } = useRouter()

    const onSuccess = async () => {
      await router.push('/campaigns')
      ElMessage.success({ message: 'Campaign has been updated' })
    }

    const onSubmit = async (campaign: ICampaignAttributes) => {
      const doSubmit = async () => {
        await safeSubmitProcessing(
          loading,
          () => campaignsStore.SaveItem(campaign),
          () => onSuccess(),
          (err) => setValidationErrors(err, errors)
        )
      }

      if (campaign.limits.length < campaignData.value.limits.length) {
        ElMessageBox.confirm(
          'Are you sure you want to remove the limit?',
          'Warning',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }
        )
          .then(() => {
            doSubmit()
          })
          .catch(() => {
            // do nothing
          })
      } else {
        doSubmit()
      }
    }

    watch(campaign, (newValue, oldValue) => {
      if (oldValue === undefined && newValue) {
        if (
          props.domain.length &&
          campaign.value &&
          !campaign.value.domainsChanged
        ) {
          campaign.value.whitelist_domains =
            campaign.value.whitelist_domains || []
          campaign.value.whitelist_domains.push(props.domain)
          campaign.value.whitelist_domains = [
            ...new Set(campaign.value.whitelist_domains)
          ]
          campaign.value.domainsChanged = true
        }
      }
      if (
        newValue &&
        (!oldValue ||
          oldValue.type_id !== newValue.type_id ||
          oldValue.stream !== newValue.stream ||
          oldValue.ron_type !== newValue.ron_type)
      ) {
        spotsListsStore.FetchItems({
          start: 0,
          limit: 1000,
          filter: {
            type_id: newValue.type_id,
            stream: newValue.stream,
            ron_type: newValue.ron_type
          }
        })
      }
    })

    return { campaign, loading, onSubmit, fetchingCampaign }
  }
})
</script>
